import React, { useState } from "react";
import { X, Check, Loader2, CheckCircle2, Sparkles, KeyRound, Mail } from "lucide-react";
import { 
  validatePassword, 
  calculateStrengthScore, 
  generateCompliantPassword, 
  PASSWORD_REQUIREMENTS 
} from "../../utils/passwordPolicy";
import { resetUserPassword } from "../../services/userService";
import { formatLastLoginDate, formatAuditTimestamp } from "../../utils/dateUtils";

export default function UserModal({
  isOpen,
  onClose,
  editingUser,
  formData,
  setFormData,
  onSubmit,
  handlePhotoChange,
  AVAILABLE_ROLES,
  handleRoleToggle,
  error,
  success,
  loading,
  onResetPassword
}) {
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetError, setResetError] = useState("");

  const handleTriggerResetPassword = async () => {
    if (!formData.email) {
      setResetError("Email address is required to reset password.");
      return;
    }
    setResetLoading(true);
    setResetMsg("");
    setResetError("");
    try {
      if (onResetPassword) {
        await onResetPassword(formData.email);
      } else {
        await resetUserPassword(formData.email);
      }
      setResetMsg(`Password reset email sent to ${formData.email}`);
      setTimeout(() => setResetMsg(""), 5000);
    } catch (err) {
      console.error("Password reset error:", err);
      setResetError(err.message || "Failed to send reset email.");
    } finally {
      setResetLoading(false);
    }
  };
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content glass-card animate-slide-up" style={{ maxWidth: "500px" }}>
        <div className="modal-header">
          <h3>{editingUser ? "Edit User Account" : "Add New User"}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        {editingUser && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.45rem 0.75rem",
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            border: "1px solid var(--border-color)",
            borderRadius: "6px",
            marginTop: "0.75rem",
            fontSize: "0.75rem"
          }}>
            <span style={{ color: "var(--text-secondary)" }}>Last Logged In:</span>
            <span 
              title={editingUser.lastLogin || editingUser.lastLoggedIn ? formatAuditTimestamp(editingUser.lastLogin || editingUser.lastLoggedIn) : ""}
              style={{ fontWeight: 600, color: (editingUser.lastLogin || editingUser.lastLoggedIn) ? "var(--primary)" : "var(--text-muted)" }}
            >
              {formatLastLoginDate(editingUser.lastLogin || editingUser.lastLoggedIn || editingUser.lastLoginAt || editingUser.lastActive)}
            </span>
          </div>
        )}
        <form onSubmit={onSubmit} style={{ marginTop: "1rem" }}>
          {/* Profile Photo Uploader Section */}
          <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
            <div style={{ position: "relative", width: "64px", height: "64px", flexShrink: 0 }}>
              {formData.photoURL ? (
                <img src={formData.photoURL} alt="Preview" style={{ width: "64px", height: "64px", borderRadius: "50%", objectFit: "cover", border: "1px solid var(--border-color)" }} />
              ) : (
                <div style={{ width: "64px", height: "64px", borderRadius: "50%", backgroundColor: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "1.5rem", color: "#ffffff" }}>
                  {formData.name ? formData.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label" htmlFor="userPhoto" style={{ marginBottom: "0.25rem" }}>Profile Picture</label>
              <input
                id="userPhoto"
                type="file"
                accept="image/*"
                className="form-input"
                onChange={handlePhotoChange}
                style={{ padding: "0.25rem 0.5rem" }}
              />
              <p className="text-secondary" style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>Max size 2MB</p>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="userName">Full Name</label>
            <input
              id="userName"
              type="text"
              required
              placeholder="e.g. John Doe"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group" style={{ marginTop: "1rem" }}>
            <label className="form-label" htmlFor="userEmail">Email Address</label>
            <input
              id="userEmail"
              type="email"
              required
              placeholder="e.g. john@atlascommand.com"
              className="form-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          {!editingUser && (
            <div className="form-group" style={{ marginTop: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.25rem" }}>
                <label className="form-label" htmlFor="userPassword" style={{ margin: 0 }}>
                  Account Password (NIST Compliant)
                </label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const compliant = generateCompliantPassword(16);
                    setFormData(prev => ({ ...prev, password: compliant }));
                  }}
                  style={{ padding: "0.2rem 0.6rem", fontSize: "0.75rem", height: "auto" }}
                >
                  <Sparkles size={12} style={{ marginRight: "0.3rem", color: "var(--primary)" }} />
                  Generate Strong (16-char)
                </button>
              </div>

              <input
                id="userPassword"
                type="text"
                required
                placeholder="Enter or generate strong password"
                className="form-input"
                value={formData.password || ""}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />

              {/* Password Strength Meter & Checklist */}
              {(() => {
                const validation = validatePassword(formData.password || "");
                const score = calculateStrengthScore(formData.password || "");
                const scoreColors = ["#ef4444", "#ef4444", "#f59e0b", "#3b82f6", "#10b981"];
                const scoreLabels = ["Empty", "Weak", "Fair", "Good", "NIST Strong & Compliant"];

                return (
                  <div style={{ marginTop: "0.5rem", background: "var(--bg-main)", border: "1px solid var(--border-color)", borderRadius: "6px", padding: "0.6rem 0.75rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.35rem" }}>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>Password Strength:</span>
                      <span style={{ fontSize: "0.75rem", fontWeight: 600, color: scoreColors[score] }}>
                        {scoreLabels[score]}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div style={{ width: "100%", height: "4px", backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden", marginBottom: "0.5rem" }}>
                      <div 
                        style={{ 
                          width: `${(score / 4) * 100}%`, 
                          height: "100%", 
                          backgroundColor: scoreColors[score],
                          transition: "width 0.3s ease, background-color 0.3s ease" 
                        }} 
                      />
                    </div>

                    {/* Policy Requirements Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.25rem 0.5rem" }}>
                      {PASSWORD_REQUIREMENTS.map((req) => {
                        const passed = validation.requirements[req.id];
                        return (
                          <div key={req.id} style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.72rem", color: passed ? "var(--success)" : "var(--text-muted)" }}>
                            {passed ? <CheckCircle2 size={12} style={{ flexShrink: 0 }} /> : <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "var(--text-muted)", margin: "3px" }} />}
                            <span>{req.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="form-group" style={{ marginTop: "1.5rem" }}>
            <label className="form-label">Assign System Roles</label>
            <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
              Select all checkboxes that apply for permissions assignment:
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
              {AVAILABLE_ROLES.map((role) => (
                <label key={role} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", padding: "0.2rem 0" }}>
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                    style={{ cursor: "pointer", width: "16px", height: "16px" }}
                  />
                  <span style={{ fontSize: "0.9rem" }}>{role}</span>
                </label>
              ))}
            </div>
          </div>

          {editingUser && (
            <div className="form-group" style={{ marginTop: "1.25rem", padding: "0.85rem", background: "var(--bg-main)", border: "1px solid var(--border-color)", borderRadius: "6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                  <label className="form-label" style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--text-primary)" }}>
                    <KeyRound size={15} style={{ color: "var(--primary)" }} />
                    Password & Security
                  </label>
                  <p className="text-secondary" style={{ fontSize: "0.75rem", marginTop: "0.2rem", margin: 0 }}>
                    Send a secure password reset email to this user.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={resetLoading}
                  onClick={handleTriggerResetPassword}
                  style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.35rem", minHeight: "auto" }}
                >
                  {resetLoading ? <Loader2 className="animate-spin" size={13} /> : <KeyRound size={13} />}
                  Reset Password
                </button>
              </div>

              {resetMsg && (
                <div className="alert alert-success" style={{ marginTop: "0.75rem", padding: "0.4rem 0.6rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <Check size={14} />
                  <span>{resetMsg}</span>
                </div>
              )}
              {resetError && (
                <div className="alert alert-danger" style={{ marginTop: "0.75rem", padding: "0.4rem 0.6rem", fontSize: "0.8rem" }}>
                  {resetError}
                </div>
              )}
            </div>
          )}

          {error && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{error}</div>}
          {success && (
            <div className="alert alert-success" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1rem" }}>
              <Check size={16} />
              <span>{success}</span>
            </div>
          )}

          <div className="modal-footer" style={{ marginTop: "2rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" size={16} /> : editingUser ? "Update User" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
